import React, { useState, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { FaCheckCircle, FaExclamationTriangle, FaInfoCircle, FaTimes, FaExclamationCircle } from 'react-icons/fa';

// Animações
const slideIn = keyframes`
  from {
    transform: translate(100%, 20px) scale(0.9);
    opacity: 0;
  }
  to {
    transform: translate(0, 0) scale(1);
    opacity: 1;
  }
`;

const slideOut = keyframes`
  from {
    transform: translate(0, 0) scale(1);
    opacity: 1;
  }
  to {
    transform: translate(100%, 20px) scale(0.9);
    opacity: 0;
  }
`;

const progressAnimation = keyframes`
  from {
    width: 100%;
  }
  to {
    width: 0%;
  }
`;

// Card da notificação
const NotificationCard = styled.div`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 12px;
  min-width: 350px;
  max-width: 500px;
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.1),
    0 8px 16px rgba(0, 0, 0, 0.05);
  pointer-events: all;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  position: relative;
  overflow: hidden;
  
  ${props => props.isExiting && css`
    animation: ${slideOut} 0.3s ease-out forwards;
  `}
  
  ${props => !props.isExiting && css`
    animation: ${slideIn} 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  `}
  
  &:hover {
    transform: translateY(-4px) scale(1.02);
    box-shadow: 
      0 25px 50px rgba(0, 0, 0, 0.15),
      0 12px 24px rgba(0, 0, 0, 0.08);
  }
  
  // Borda colorida baseada no tipo
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: ${props => {
      switch (props.type) {
        case 'success': return 'linear-gradient(135deg, #2ecc71, #27ae60)';
        case 'error': return 'linear-gradient(135deg, #e74c3c, #c0392b)';
        case 'warning': return 'linear-gradient(135deg, #f39c12, #e67e22)';
        case 'info': return 'linear-gradient(135deg, #3498db, #2980b9)';
        default: return 'linear-gradient(135deg, #667eea, #764ba2)';
      }
    }};
  }
  
  @media (max-width: 768px) {
    min-width: auto;
    max-width: none;
    margin: 0 0 12px 0;
    padding: 16px;
  }
`;

// Conteúdo da notificação
const NotificationContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

// Ícone
const IconContainer = styled.div`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => {
    switch (props.type) {
      case 'success': return '#27ae60';
      case 'error': return '#e74c3c';
      case 'warning': return '#f39c12';
      case 'info': return '#3498db';
      default: return '#667eea';
    }
  }};
  font-size: 20px;
`;

// Texto
const TextContainer = styled.div`
  flex: 1;
  min-width: 0;
`;

const Title = styled.div`
  font-weight: 600;
  font-size: 0.95rem;
  color: #1e293b;
  margin-bottom: 4px;
  line-height: 1.4;
`;

const Message = styled.div`
  font-size: 0.85rem;
  color: #64748b;
  line-height: 1.5;
  word-wrap: break-word;
`;

// Botão de fechar
const CloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(100, 116, 139, 0.1);
  border: none;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #64748b;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(100, 116, 139, 0.2);
    color: #1e293b;
    transform: scale(1.1);
  }
`;

// Barra de progresso
const ProgressBar = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  background: ${props => {
    switch (props.type) {
      case 'success': return 'linear-gradient(90deg, #2ecc71, #27ae60)';
      case 'error': return 'linear-gradient(90deg, #e74c3c, #c0392b)';
      case 'warning': return 'linear-gradient(90deg, #f39c12, #e67e22)';
      case 'info': return 'linear-gradient(90deg, #3498db, #2980b9)';
      default: return 'linear-gradient(90deg, #667eea, #764ba2)';
    }
  }};
  border-radius: 0 0 16px 16px;
  
  ${props => props.duration && !props.isPaused && css`
    animation: ${progressAnimation} ${props.duration}ms linear;
  `}
  
  ${props => props.isPaused && css`
    animation-play-state: paused;
  `}
`;

// Componente principal
const ModernNotification = ({ 
  id,
  type = 'info', 
  title, 
  message, 
  duration = 5000,
  onClose,
  showCloseButton = true,
  pauseOnHover = true 
}) => {
  const [isExiting, setIsExiting] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [timeLeft, setTimeLeft] = useState(duration);

  // Ícones por tipo
  const getIcon = () => {
    switch (type) {
      case 'success': return <FaCheckCircle />;
      case 'error': return <FaExclamationCircle />;
      case 'warning': return <FaExclamationTriangle />;
      case 'info': return <FaInfoCircle />;
      default: return <FaInfoCircle />;
    }
  };

  // Títulos padrão por tipo
  const getDefaultTitle = () => {
    switch (type) {
      case 'success': return 'Sucesso!';
      case 'error': return 'Erro!';
      case 'warning': return 'Atenção!';
      case 'info': return 'Informação';
      default: return 'Notificação';
    }
  };

  // Timer para auto-close
  useEffect(() => {
    if (duration === 0) return; // Não fecha automaticamente

    let timer;
    let startTime = Date.now();
    let remainingTime = timeLeft;

    const tick = () => {
      if (isPaused) {
        startTime = Date.now();
        return;
      }

      const elapsed = Date.now() - startTime;
      const newTimeLeft = Math.max(0, remainingTime - elapsed);
      
      setTimeLeft(newTimeLeft);

      if (newTimeLeft <= 0) {
        handleClose();
      } else {
        timer = requestAnimationFrame(tick);
      }
    };

    timer = requestAnimationFrame(tick);

    return () => {
      if (timer) {
        cancelAnimationFrame(timer);
      }
    };
  }, [isPaused, timeLeft, duration]);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      onClose && onClose(id);
    }, 300); // Tempo da animação de saída
  };

  const handleMouseEnter = () => {
    if (pauseOnHover) {
      setIsPaused(true);
    }
  };

  const handleMouseLeave = () => {
    if (pauseOnHover) {
      setIsPaused(false);
    }
  };

  return (
    <NotificationCard
      type={type}
      isExiting={isExiting}
      onClick={handleClose}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <NotificationContent>
        <IconContainer type={type}>
          {getIcon()}
        </IconContainer>
        
        <TextContainer>
          <Title>{title || getDefaultTitle()}</Title>
          {message && <Message>{message}</Message>}
        </TextContainer>
      </NotificationContent>

      {showCloseButton && (
        <CloseButton onClick={(e) => {
          e.stopPropagation();
          handleClose();
        }}>
          <FaTimes size={12} />
        </CloseButton>
      )}

      {duration > 0 && (
        <ProgressBar 
          type={type} 
          duration={duration}
          isPaused={isPaused}
        />
      )}
    </NotificationCard>
  );
};

export default ModernNotification;
